import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, type FilterSpec } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { countryName, humanDate, messageFor, money, stamp } from "./format";
import { csvName, exportPriceLog, filterState, hadNoFreeTier, isoDay, isRemovedFreeTier, LOGS_COPY, LOGS_PAGE_SIZE, marketLabel, paramsFrom, placeLabel, priceCsv, priceLogQuery, priceSourceLabel, SUBJECT_LABEL, SUBJECT_TONE, type PriceLogRow } from "./logs";
import { LogsError, LogsToolbar } from "./LogsToolbar";
import { SnapshotLine } from "./SnapshotLine";

const COUNT = new Intl.NumberFormat("en-GB");
const C = LOGS_COPY.price;

/** The Price pane: one server page of the append-only Price Log, newest commit first, with the facets as filters. */
export function PriceLogPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const params = paramsFrom(search);
  const page = useQuery(priceLogQuery(params));
  const heading = <div><h2 className="text-lg font-semibold text-ks-ink">{C.title}</h2><p className="text-sm text-ks-grey-1">{C.note}</p></div>;
  if (page.isPending) return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  if (page.error) return <div className="flex flex-col gap-3">{heading}<LogsError message={messageFor(page.error, areaName)} onRetry={() => void page.refetch()} /></div>;
  const d = page.data;
  const filters = filterState(params, [params.subject, params.source]);
  if (d.page.total === 0 && filters === "none") {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{C.emptyTitle}</p><p className="text-ks-grey-1">{C.emptyBody}</p></CardContent></Card></div>;
  }
  const paneFilters: FilterSpec[] = [
    { name: "subject", label: C.filters.subject, kind: "choice", anyLabel: "All", options: d.facets.subjects.map((s) => ({ value: s, label: SUBJECT_LABEL[s] })) },
    { name: "source", label: C.filters.source, kind: "choice", anyLabel: "All", options: d.facets.sources.map((s) => ({ value: s, label: C.sourceLabel[s] })) },
  ];
  const columns: ColumnDef<PriceLogRow, unknown>[] = [
    { id: "date", header: C.columns[0], cell: ({ row: r }) => <span className="whitespace-nowrap text-sm">{stamp(r.original.committed_at)}</span> },
    { id: "change", header: C.columns[1], cell: ({ row: r }) => <Badge variant="outline" className={SUBJECT_TONE[r.original.subject_type]}>{SUBJECT_LABEL[r.original.subject_type]}</Badge> },
    { id: "subject", header: C.columns[2], cell: ({ row: r }) => <SubjectCell row={r.original} /> },
    { id: "market", header: C.columns[3], cell: ({ row: r }) => marketLabel(r.original.market) },
    { id: "new", header: C.columns[4], meta: { align: "right" }, cell: ({ row: r }) => <ValueCell row={r.original} /> },
    { id: "previous", header: C.columns[5], meta: { align: "right" }, cell: ({ row: r }) => r.original.previous_value == null ? <span className="text-ks-grey-1">{hadNoFreeTier(r.original) ? C.noFreeTier : C.first}</span> : <span className="tabular-nums text-ks-grey-1">{money(r.original.previous_value, r.original.currency)}</span> },
    { id: "source", header: C.columns[6], cell: ({ row: r }) => <SourceCell row={r.original} /> },
  ];
  const exportCsv = async () => ({ text: priceCsv((await exportPriceLog(params)).rows), name: csvName("price", isoDay(new Date())) });
  return (
    <div className="flex flex-col gap-3">
      {heading}
      <LogsToolbar areaName={areaName} pane="price" search={search} go={go} params={params} facets={d.facets} paneFilters={paneFilters} applied={d.filters}
        total={d.page.total} trailing={C.trailing(COUNT.format(d.page.total))} exportCsv={exportCsv} />
      <DataTable columns={columns} rows={d.rows} page={{ pageIndex: params.page, pageSize: LOGS_PAGE_SIZE, rowCount: d.page.total }}
        onPageChange={(next) => go({ ...search, pp_lpage: next || undefined })} caption={C.title} emptyMessage={filters === "sku" ? LOGS_COPY.noHistorySku : C.noMatch} density="compact" />
      <p className="text-xs text-ks-grey-1">{C.footnote}</p>
    </div>
  );
}

function SubjectCell({ row }: { row: PriceLogRow }) {
  if (row.subject_type === "free_shipping_threshold") return <span className="text-ks-ink">{C.country(countryName(row.country))}</span>;
  return <div><span className="font-semibold text-ks-ink">{row.product ?? row.xero_sku} {row.variant ?? ""}</span><div className="text-xs text-ks-grey-1">{row.xero_sku} · {placeLabel(row)}</div></div>;
}
function ValueCell({ row }: { row: PriceLogRow }) {
  if (isRemovedFreeTier(row)) return <span className="font-semibold text-ks-grey-1">{C.noFreeTier}</span>;
  return <span className="font-semibold tabular-nums text-ks-ink">{money(row.new_value, row.currency)}</span>;
}
function SourceCell({ row }: { row: PriceLogRow }) {
  return (
    <div>
      <span className="text-sm">{priceSourceLabel(row)}</span>
      {row.source === "price_run" && row.comment && <div className="text-xs text-ks-warn">{C.override(row.comment)}</div>}
      {/* C1 v1.50: a temporary price names its until date and reason; its return names the reason of the override it closes. */}
      {row.source === "price_run" && row.override_until && <div className="text-xs text-ks-purple" data-log-override>{C.temporary(humanDate(row.override_until), row.override_reason ?? "")}</div>}
      {row.source === "override_return" && row.comment && <div className="text-xs text-ks-grey-1" data-log-return>{C.returnLine(row.comment)}</div>}
      {row.source === "legacy_sheet" && row.comment && <div className="text-xs text-ks-grey-1">{C.legacyNote(row.comment)}</div>}
      {/* C1 v1.14: a price entry's rule verdicts at commit, or "no snapshot recorded"; a threshold entry has no strategy. */}
      {row.subject_type === "price" && <SnapshotLine snapshot={row.strategy_snapshot} />}
    </div>
  );
}
